import { cn } from '@/lib/utils'

export type RadioOption = {
  value: string
  label: string
}

export type RadioGroupFieldProps = {
  id: string
  label: string
  name: string
  value?: string
  onValueChange: (value: string) => void
  options: RadioOption[]
  error?: string
  layout?: 'stacked' | 'inline'
  className?: string
}

export const RadioGroupField = ({
  id,
  label,
  name,
  value,
  onValueChange,
  options,
  error,
  layout = 'stacked',
  className,
}: RadioGroupFieldProps) => {
  const isInline = layout === 'inline'

  return (
    <div
      className={cn(
        isInline ? 'flex flex-row items-center gap-4' : 'flex flex-col gap-2',
        className
      )}
    >
      <span
        id={id}
        className={cn(
          'text-sm font-semibold text-foreground',
          isInline && 'w-44 shrink-0'
        )}
      >
        {label}
      </span>

      <div className={cn('flex min-w-0 flex-col gap-2', isInline && 'flex-1')}>
        <div
          className="flex flex-row gap-1"
          role="radiogroup"
          aria-labelledby={id}
          aria-invalid={Boolean(error)}
        >
          {options.map((option) => {
            const optionId = `${id}-${option.value}`

            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-button-sm border border-foreground/10 bg-surface-muted px-4 py-4 text-sm text-foreground transition-colors has-[:focus-visible]:border-primary-focus has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-focus/30"
              >
                <input
                  id={optionId}
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={() => onValueChange(option.value)}
                  className="size-5 shrink-0 accent-foreground"
                />
                {option.label}
              </label>
            )
          })}
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  )
}
